import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { logSecurityEvent } from '@/lib/security';

interface AdminSession {
  startTime: number;
  lastActivity: number;
  warningShown: boolean;
}

const ADMIN_SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const WARNING_THRESHOLD = 25 * 60 * 1000; // 25 minutes
const ACTIVITY_CHECK_INTERVAL = 60 * 1000; // 1 minute

export const useAdminSecurity = () => {
  const { user, isAdmin, signOut } = useAuth();
  const [session, setSession] = useState<AdminSession | null>(null);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  // Initialize admin session
  useEffect(() => {
    if (user && isAdmin) {
      const newSession: AdminSession = {
        startTime: Date.now(),
        lastActivity: Date.now(),
        warningShown: false
      };
      setSession(newSession);
      
      logSecurityEvent('admin_session_started', {
        userId: user.id,
        sessionStart: newSession.startTime
      }, 'low');
    } else {
      setSession(null);
      setShowTimeoutWarning(false);
    }
  }, [user, isAdmin]);

  // Track user activity
  useEffect(() => {
    if (!session) return;

    const updateActivity = () => {
      setSession(prev => prev ? { ...prev, lastActivity: Date.now() } : null);
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, [session]);

  // Session timeout monitoring
  useEffect(() => {
    if (!session || !user || !isAdmin) return;

    const checkSession = () => {
      const now = Date.now();
      const timeSinceActivity = now - session.lastActivity;

      if (timeSinceActivity >= ADMIN_SESSION_TIMEOUT) {
        // Force logout
        logSecurityEvent('admin_session_timeout', {
          userId: user.id,
          sessionDuration: now - session.startTime,
          lastActivity: session.lastActivity
        }, 'medium');
        
        signOut();
        setShowTimeoutWarning(false);
      } else if (timeSinceActivity >= WARNING_THRESHOLD && !session.warningShown) {
        // Show warning
        setShowTimeoutWarning(true);
        setSession(prev => prev ? { ...prev, warningShown: true } : null);
        
        logSecurityEvent('admin_session_warning', {
          userId: user.id,
          timeRemaining: ADMIN_SESSION_TIMEOUT - timeSinceActivity
        }, 'low');
      }
    };

    const interval = setInterval(checkSession, ACTIVITY_CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [session, user, isAdmin, signOut]);

  const extendSession = () => {
    if (session) {
      setSession(prev => prev ? {
        ...prev,
        lastActivity: Date.now(),
        warningShown: false
      } : null);
      setShowTimeoutWarning(false);
      
      if (user) {
        logSecurityEvent('admin_session_extended', {
          userId: user.id,
          extendedAt: Date.now()
        }, 'low');
      }
    }
  };

  const forceLogout = () => {
    if (user) {
      logSecurityEvent('admin_session_manual_logout', {
        userId: user.id,
        sessionDuration: session ? Date.now() - session.startTime : 0
      }, 'low');
    }
    signOut();
  };

  const getTimeRemaining = () => {
    if (!session) return 0;
    const timeSinceActivity = Date.now() - session.lastActivity;
    return Math.max(0, ADMIN_SESSION_TIMEOUT - timeSinceActivity);
  };

  return {
    session,
    showTimeoutWarning,
    extendSession,
    forceLogout,
    getTimeRemaining,
    isSessionValid: !!session && user && isAdmin
  };
};